import { createContext, useContext, useMemo } from 'react';
import { Outlet, Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Eye } from 'lucide-react';
import { PortalSidebar } from './PortalSidebar';
import type { PortalCounts } from './PortalSidebar';
import { api } from '@/lib/api';
import { demandaDoCliente } from '@/lib/distribuicao';
import { useAuth } from '@/lib/auth';

/** Context para propagar o solicitanteId efetivo (staff "as" override ou JWT) */
const PortalSidContext = createContext<string | null>(null);
export function usePortalSid() { return useContext(PortalSidContext); }

export function PortalLayout() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const asSid = params.get('as');
  const sid = asSid ?? user?.solicitanteId ?? null;
  const isStaff = !!user && user.papel !== 'Cliente';

  const demandasQ = useQuery({ queryKey: ['portal', 'demandas'], queryFn: () => api.demandas.list().then(r => r.data) });
  const eventosQ = useQuery({ queryKey: ['portal', 'eventos'], queryFn: () => api.eventos.listPortal().then(r => r.data) });
  const escopoQ = useQuery({ queryKey: ['portal', 'escopo'], queryFn: () => api.escopoEntregas.list().then(r => r.data) });
  const solicitantesQ = useQuery({ queryKey: ['solicitantes'], queryFn: () => api.solicitantes.list().then(r => r.data), enabled: !!asSid });

  const solicitanteNome = useMemo(() => {
    if (!asSid) return user?.nome ?? '';
    return (solicitantesQ.data ?? []).find(s => s.id === asSid)?.nome ?? asSid;
  }, [asSid, solicitantesQ.data, user]);

  const counts: PortalCounts = useMemo(() => {
    const demandas = (demandasQ.data ?? []).filter(d => !sid || demandaDoCliente(d, sid));
    const eventos = eventosQ.data ?? [];
    const escopo = escopoQ.data ?? [];
    const meusEventos = eventos.filter(e => (e.solicitantesIds ?? []).includes(sid ?? ''));
    const galeria = meusEventos.filter(e => e.status === 'Realizado').length;
    const escopoAprovacao = escopo.filter(e => e.status === 'Aguardando aprovação').length;
    return {
      praAprovar: demandas.filter(d => d.status === 'Aguardando aprovação').length + escopoAprovacao,
      entregas: demandas.length,
      eventos: meusEventos.length,
      galeria,
      oportunidades: eventos.length - meusEventos.length,
    };
  }, [demandasQ.data, eventosQ.data, escopoQ.data, sid]);

  return (
    <PortalSidContext.Provider value={sid}>
      <div className="min-h-screen bg-bg text-text">
        <PortalSidebar counts={counts} />
        <main className="ml-[232px]">
          {isStaff && (
            <div className="sticky top-0 z-30 bg-blue/10 backdrop-blur-sm border-b border-blue/30 px-4 py-1.5 flex items-center gap-3 text-xs">
              <span className="inline-flex items-center gap-1.5 text-blue-light font-semibold">
                <Eye className="w-3.5 h-3.5" />
                Visualização staff
              </span>
              <span className="text-text-2 truncate">
                vendo como cliente {solicitanteNome}
              </span>
              <Link
                to="/portais"
                className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 bg-panel hover:bg-panel-2 border border-border rounded text-text-2 hover:text-text transition-colors font-semibold"
              >
                <ArrowLeft className="w-3 h-3" />
                Voltar pra plataforma
              </Link>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </PortalSidContext.Provider>
  );
}
